import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { forgotPassword } from '../features/auth/authApi';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');

  const submit = useMutation({
    mutationFn: () => forgotPassword(email),
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    submit.mutate();
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-paper-100 px-4 font-manuscript">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #8c2d1e 1.5px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className="relative w-full max-w-sm">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
            <img src="/logo.png" alt={t('common.appName')} className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-lg font-semibold text-manuscript-ink">{t('common.appName')}</span>
        </Link>

        <div className="rounded-sm border border-manuscript-line-strong bg-paper-50 p-8">
          <h1 className="font-display text-xl font-semibold text-manuscript-ink">
            {t('auth.forgotPassword.title')}
          </h1>
          <p className="mt-1 text-sm text-manuscript-ink-soft">{t('auth.forgotPassword.subtitle')}</p>

          {submit.isSuccess ? (
            <p className="mt-6 text-sm text-manuscript-ink-soft">{t('auth.forgotPassword.sent')}</p>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
              <div>
                <label htmlFor="email" className="text-sm text-manuscript-ink-soft">
                  {t('common.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 w-full rounded-sm border border-manuscript-line-strong bg-paper-100 px-4 py-2.5 text-sm text-manuscript-ink outline-none focus:border-seal-500"
                />
              </div>

              <button
                type="submit"
                disabled={submit.isPending}
                className="mt-2 flex w-full items-center justify-center rounded-sm bg-seal-500 px-6 py-3 text-sm font-semibold text-paper-50 transition-colors hover:bg-seal-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submit.isPending ? t('common.loading') : t('auth.forgotPassword.submit')}
              </button>

              {submit.isError && <p className="text-sm text-red-500">{t('common.error.generic')}</p>}
            </form>
          )}

          <p className="mt-5 text-center text-sm text-manuscript-ink-soft">
            <Link to="/login" className="font-medium text-seal-500 hover:text-seal-400">
              {t('auth.checkEmail.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
